import { CallState } from "@b2bportal/core-types";
import { FixedPointsPricingVersion } from "@b2bportal/rbc-accounts-api";
import { createSlice } from "@reduxjs/toolkit";
import { fetchPointsBalance } from "../account/thunks/fetchPointsBalance";
import { optOutOfGridV2 } from "./thunks/optOutOfGridV2";
import type { GridV2StateType, GridV2Version } from "./types";

const initialGridV2State: GridV2StateType = {
  effectiveVersion: undefined,
  isOptedOut: false,
  callState: CallState.NotCalled,
};

const isGridV2Version = (value: unknown): value is GridV2Version =>
  value === FixedPointsPricingVersion.V1 ||
  value === FixedPointsPricingVersion.V2;

/**
 * Where the app learns which grid prices this user's flights. The BFF puts
 * the effective version (LD variant AND not opted out) on the points balance
 * response, so this slice has no fetch of its own: it listens to
 * `fetchPointsBalance`, which Layout dispatches on mount, and to the opt-out.
 */
export const gridV2Slice = createSlice({
  name: "gridV2",
  initialState: initialGridV2State,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPointsBalance.pending, (state) => {
        state.callState = CallState.InProcess;
      })
      .addCase(fetchPointsBalance.fulfilled, (state, action) => {
        // A balance call that was in flight when the user opted out answers
        // with the pre-opt-out version; the opt-out is sticky, so it wins.
        if (state.isOptedOut) {
          state.callState = CallState.Success;
          return;
        }
        const { fixedPointsPricingVersion, pricingExperimentOptedOut } =
          action.payload;
        // An older BFF answers without the fields: no version, so V1 (R6).
        state.effectiveVersion = isGridV2Version(fixedPointsPricingVersion)
          ? fixedPointsPricingVersion
          : undefined;
        state.isOptedOut = pricingExperimentOptedOut === true;
        state.callState = CallState.Success;
      })
      .addCase(fetchPointsBalance.rejected, (state, action) => {
        // The thunk aborts itself when a call is already in flight; that call
        // will answer, so the abort must not touch the state.
        if (action.meta.aborted) return;
        // A failed call never grants V2 (R6), but a failed refresh does not
        // take away a version the BFF already granted: a network blip must
        // not flip a V2 user's merchandising mid-session.
        state.callState = CallState.Failed;
      })
      .addCase(optOutOfGridV2.fulfilled, (state) => {
        // Sticky and idempotent on the BFF side: V1 is the only outcome,
        // whatever the body says.
        state.effectiveVersion = FixedPointsPricingVersion.V1;
        state.isOptedOut = true;
        state.callState = CallState.Success;
      });
  },
});

export const GridV2Actions = gridV2Slice.actions;
export const gridV2Reducer = gridV2Slice.reducer;
